import { use, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalApp.css';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';
import { Share2, FileText, RefreshCcw, CheckCircle, Clock, Eye, Download, Upload, Trash } from "lucide-react";

// Remembers which row the user acted on and its on-screen position, so returning
// here from a case screen (mirrors the mobile Case List's behavior) scrolls back
// to and highlights that row instead of dropping the user at the top of the list.
const SELECTED_KEY = 'mycases_selected_web';

const MyCases = ({ API_URL }) => {
  const navigate = useNavigate();
  const [firList, setFirList] = useState([]);
  const [noCasesFound, setNoCasesFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [firToDelete, setFirToDelete] = useState(null);
  const [firToShare, setFirToShare] = useState(null);
  const [showShareConfirmPopup, setShowShareConfirmPopup] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareErrorMessage, setShareErrorMessage] = useState(null);
  const [shareSuccessMessage, setShareSuccessMessage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState(null);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState(null);

  const [confirmMessage, setConfirmMessage] = useState(null);

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFIRID, setDownloadFIRID] = useState(null);

  const email = localStorage.getItem('email');
  var isAdmin = localStorage.getItem('role') === 'admin';

  const fetchFirList = async () => {
    try {
      if (localStorage.getItem('firList')) {
        setFirList(JSON.parse(localStorage.getItem('firList')));
      }
    } catch (error) {
      console.error("Error parsing FIR list from localStorage:", error);
    }

    setIsLoading(true);
    setErrorMessage(null);
    var jwt = localStorage.getItem('jwtToken');

    try {
      const response = await axios.get(API_URL + `/fir/list`, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      //console.log("FIR List: " + response.data);
      if (response.data != null) {
        // Sort the list before storing and setting state
        const sortedList = sortFirList(response.data);
        localStorage.setItem('firList', JSON.stringify(sortedList));
        setFirList(sortedList);
      } else {
        console.log("No FIR found");
        setNoCasesFound(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setErrorMessage("An error occurred while fetching the list of FIRs. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('firList')) {
      setFirList(JSON.parse(localStorage.getItem('firList')));
      return;
    }

    fetchFirList();
  }, [API_URL]);

  // const handleViewCase = (fir) => {
  //   if (!fir.summaryAnalysed && !fir.complainant.name && !fir.accusedList && !fir.firSections) {
  //     // Call API to analyse FIR
  //     analyseFIR(fir);
  //   } else {
  //     if (fir.matrimonialMatter)
  //       navigate('/fir', { state: { API_URL: API_URL, data: fir } });
  //     else
  //       navigate('/investigate/fir', { state: { API_URL: API_URL, fir: fir } });
  //   }
  // };

  // Records which row the user is navigating away from and its position on
  // screen, so we can scroll back to that exact spot and highlight it on return.
  const selectCase = (fir) => {
    const rowEl = document.querySelector(`[data-case-id="${fir.id}"]`);
    const offset = rowEl ? rowEl.getBoundingClientRect().top : 0;
    try {
      localStorage.setItem(SELECTED_KEY, JSON.stringify({ id: fir.id, offset }));
    } catch {
      // ignore storage failures - highlighting/scroll-restore is a nice-to-have
    }
  };

  const selectedId = (() => {
    try {
      return JSON.parse(localStorage.getItem(SELECTED_KEY) || 'null')?.id;
    } catch {
      return null;
    }
  })();

  // Restore the last-selected row's on-screen position and highlight once the
  // list has rendered, so returning from a case screen feels like you never left.
  useEffect(() => {
    if (!firList || firList.length === 0) return;

    const stored = localStorage.getItem(SELECTED_KEY);
    if (!stored) return;

    let selected;
    try {
      selected = JSON.parse(stored);
    } catch {
      return;
    }

    const rowEl = document.querySelector(`[data-case-id="${selected.id}"]`);
    if (!rowEl) return;

    // Compute the row's absolute document position (viewport-relative top + current
    // scroll) so this is idempotent no matter how many times it runs - React 18
    // StrictMode double-invokes effects in dev, and a relative scrollBy would overshoot.
    const absoluteRowTop = rowEl.getBoundingClientRect().top + window.scrollY;
    window.scrollTo(0, absoluteRowTop - (selected.offset || 0));
  }, [firList]);

  const handleViewCase = async (fir) => {
    selectCase(fir);
    navigate('/fir/dashboard', { state: { API_URL: API_URL, fir } });

    /*
    setIsLoading(true);
    setErrorMessage("");

    var jwt = localStorage.getItem('jwtToken');

    try {
      const response = await axios.get(API_URL + `/fir/` + fir.id, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`,
        },
      });

      if (response.status === 200) {
        localStorage.removeItem('firList');
        navigate('/fir/dashboard', { state: { API_URL: API_URL, fir: response.data } });
        // if (response.data.matrimonialMatter && response.data.matrimonialMatter === 'Yes') {
        //   navigate('/fir', { state: { API_URL: API_URL, data: response.data } });
        // } else {
        //   navigate('/investigate/fir', { state: { API_URL: API_URL, fir: response.data } });
        // }
      } else {
        setErrorMessage("Error processing request. Please try again.");
      }
    } catch (error) {
      setErrorMessage("Error processing request. Please try again.");
    } finally {
      setIsLoading(false);
    }
      */
  };

  const analyseFIR = async (fir) => {
    setIsLoading(true);
    setErrorMessage("");

    var jwt = localStorage.getItem('jwtToken');

    try {
      const response = await axios.get(API_URL + `/fir/summarize/` + fir.id, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`,
        },
      });

      if (response.status === 200) {
        localStorage.removeItem('firList');
        navigate('/fir/dashboard', { state: { API_URL: API_URL, fir: response.data } });
        // if (response.data.matrimonialMatter && response.data.matrimonialMatter === 'Yes') {
        //   navigate('/fir', { state: { API_URL: API_URL, data: response.data } });
        // } else {
        //   navigate('/investigate/fir', { state: { API_URL: API_URL, fir: response.data } });
        // }
      } else {
        setErrorMessage("Error processing request. Please try again.");
      }
    } catch (error) {
      setErrorMessage("Error processing request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewFIR = (fir) => {
    //console.log("View fir: ", fir.id);
    selectCase(fir);
    navigate('/fir/complaint', { state: { API_URL: API_URL, fromPage: 'Case List', data: fir } });
  };

  const handleDeleteClick = (fir) => {
    setFirToDelete(fir);
    setShowDeleteConfirmPopup(true);
  };

  const handleShareClick = (fir) => {
    setFirToShare(fir);
    setShowShareConfirmPopup(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmPopup(false);
    setFirToDelete(null);
    setDeleteErrorMessage(null);
    setDeleteSuccessMessage(null);
    setIsDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!firToDelete) return;

    setIsDeleting(true);
    setDeleteErrorMessage(null);
    setDeleteSuccessMessage(null);

    try {
      var jwt = localStorage.getItem('jwtToken');
      await axios.delete(API_URL + `/fir/${firToDelete.id}`, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });

      const updatedFirList = firList.filter((fir) => fir.id !== firToDelete.id);
      const sortedList = sortFirList(updatedFirList);
      setFirList(sortedList);
      localStorage.setItem('firList', JSON.stringify(sortedList));
      setDeleteSuccessMessage('FIR deleted successfully.');
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setDeleteErrorMessage('You are not authorised to delete the FIR.');
      } else {
        setDeleteErrorMessage("An error occurred while deleting the FIR. Please try again later.");
      }
    } finally {
      setIsDeleting(false);
    }
  };


  const handleConfirmShare = async () => {
    if (!firToShare) return;

    setShareLoading(true);
    setShareErrorMessage(null);
    setShareSuccessMessage(null);

    try {
      var jwt = localStorage.getItem('jwtToken');
      var request = {
        email: shareEmail
      };
      await axios.post(API_URL + `/fir/share/${firToShare.id}`, request, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      });
      setShareSuccessMessage(`FIR shared successfully with ${shareEmail}`);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setShareErrorMessage('You are not authorised to share the FIR.');
      } else {
        setShareErrorMessage('An error occurred while processing the request. Please try again later.');
      }
    } finally {
      setShareLoading(false);
    }
  };

  const handleDownloadClick = async (fir) => {
    //setIsLoading(true); // we will use isDownloading instead
    setIsDownloading(true); //set this state variable to true
    setDownloadFIRID(fir.id); // Set the FIR ID to be downloaded

    setDownloadHover(null);

    try {
      const jwt = localStorage.getItem('jwtToken');
      const response = await axios.get(API_URL + `/fir/download/${fir.id}`, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
        responseType: 'blob', // Important: This tells axios to expect a binary file
      });

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = `FIR_${fir.id}.pdf`; // You can customize the file name here
      link.style.display = 'none'; // Hide the link

      // Add the link to the DOM, click it, and then remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the temporary URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading FIR:', error);
      setErrorMessage("An error occurred while downloading the FIR. Please try again later.");
    } finally {
      //setIsLoading(false); // we will use isDownloading instead
      setIsDownloading(false); //set this state variable to false
    }
  };

  function formatDateTime(dateTime) {
    //dateTime format 24032025 15:00
    const date = dateTime.substring(0, 2);
    const month = dateTime.substring(2, 4);
    const year = dateTime.substring(4, 8);
    return `${date}-${month}-${year}`;
  };

  // Function to sort the FIR list by uploadedOn in descending order (latest first)
  const sortFirList = (list) => {
    return [...list].sort((a, b) => {
      // Convert the date strings to comparable values
      const dateA = parseDateTime(a.uploadedOn);
      const dateB = parseDateTime(b.uploadedOn);
      return dateB - dateA; // Descending order
    });
  };

  // Helper function to parse the date and time string
  const parseDateTime = (dateTime) => {
    if (!dateTime || dateTime === "") {
      return 0; // Handle cases where uploadedOn is null or undefined
    }

    const day = parseInt(dateTime.substring(0, 2), 10);
    const month = parseInt(dateTime.substring(2, 4), 10) - 1; // Month is 0-indexed
    const year = parseInt(dateTime.substring(4, 8), 10);
    const hours = parseInt(dateTime.substring(9, 11), 10);
    const minutes = parseInt(dateTime.substring(12, 14), 10);
    return new Date(year, month, day, hours, minutes).getTime();
  };

  const sampleFIR = ['0dkuEsSZ1Hs9xIMjMqCf', 'y9z9xpAUJewPhl5JEfHD'];
  //const showcaseFIR = ['gQeWSBDlgsC8Y48njpJW', '9Kz9JEha6yARfSg99exq', 'soGXbBb04x3FDPs8BPEu', '0xwtQdCCIiYc6XDZ2715', 'IgF52ka1qzXbGFzAfXuq', 'fPBTUwrNWT2YI4RHdHEt', 'tejy2Gr63G4He27AThgO', 'NJjHcugTpGTZMGlYkKqP'];
  const showcaseFIR = ['gQeWSBDlgsC8Y48njpJW', '9Kz9JEha6yARfSg99exq'];

  const [viewHover, setViewHover] = useState(null);
  const [iqDownloadHover, setIQDownloadHover] = useState(null);
  const [irUploadHover, setIRUploadHover] = useState(null);
  const [firViewHover, setFirViewHover] = useState(null);
  const [downloadHover, setDownloadHover] = useState(null);
  const [deleteHover, setDeleteHover] = useState(null);
  const [shareHover, setShareHover] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]); // holds up to 2 fir.id values
  const maxSelected = selectedIds.length >= 2;

  const toggleSelection = (id, checked) => {
    setSelectedIds((prev) => {
      // remove
      if (!checked) return prev.filter((x) => x !== id);
      // add (enforce max two)
      if (prev.includes(id)) return prev;            // already selected
      if (prev.length >= 2) return prev;            // ignore 3rd select
      return [...prev, id];
    });
  };

  const [pairNotFound, setPairNotFound] = useState(false);
  const [pairSelected, setPairSelected] = useState(null);
  const [pairMissing, setPairMissing] = useState(null);

  const selectPairs = (id, checked) => {
    if (!checked) {
      setSelectedIds([]);
      setPairNotFound(false);
      return;
    }

    var primaryFIR;
    var secondaryFIR;

    for (var i = 0; i < firList.length; i++) {
      if (firList[i].id === id) {
        primaryFIR = firList[i];
      }
    }

    for (var i = 0; i < firList.length; i++) {
      if (firList[i].id === id) {
        continue;
      }
      if (firList[i].complainant.name === primaryFIR.complainant.name && firList[i].accusedList[0].name === primaryFIR.accusedList[0].name) {
        secondaryFIR = firList[i];
        break;
      }
    }

    if (secondaryFIR !== undefined) {
      setSelectedIds([id, secondaryFIR.id]);
    } else {
      setPairNotFound(true);
      if (primaryFIR.user === 'Accused' || primaryFIR.user === 'Defense Counsel') {
        setPairSelected('Accused');
        setPairMissing('Complainant');
      } else {
        setPairSelected('Complainant');
        setPairMissing('Accused');
      }
    }
  }

  const [downloadIQ, setDownloadIQ] = useState(null);
  const [isDownloadingIQ, setIsDownloadingIQ] = useState(false);

  const handleDownloadIQClick = (fir) => {
    var id = fir.id;

    var primaryFIR;
    var secondaryFIR;

    for (var i = 0; i < firList.length; i++) {
      if (firList[i].id === id) {
        primaryFIR = firList[i];
      }
    }

    for (var i = 0; i < firList.length; i++) {
      if (firList[i].id === id) {
        continue;
      }
      if (firList[i].complainant.name === primaryFIR.complainant.name && firList[i].accusedList[0].name === primaryFIR.accusedList[0].name) {
        secondaryFIR = firList[i];
        break;
      }
    }

    if (secondaryFIR !== undefined) {
      setSelectedIds([id, secondaryFIR.id]);
      setDownloadIQ(fir);
    } else {
      setPairNotFound(true);
    }
  }

  const handleUploadIRClick = (fir) => {
    var id = fir.id;

    var primaryFIR;
    var secondaryFIR;

    for (var i = 0; i < firList.length; i++) {
      if (firList[i].id === id) {
        primaryFIR = firList[i];
      }
    }

    for (var i = 0; i < firList.length; i++) {
      if (firList[i].id === id) {
        continue;
      }
      if (firList[i].complainant.name === primaryFIR.complainant.name && firList[i].accusedList[0].name === primaryFIR.accusedList[0].name) {
        secondaryFIR = firList[i];
        break;
      }
    }

    if (secondaryFIR !== undefined) {
      setSelectedIds([id, secondaryFIR.id]);
      setUploadIR(fir);
    } else {
      setPairNotFound(true);
      if (primaryFIR.user === 'Accused' || primaryFIR.user === 'Defense Counsel') {
        setPairSelected('Accused');
        setPairMissing('Complainant');
      } else {
        setPairSelected('Complainant');
        setPairMissing('Accused');
      }
    }
  }

  const handleDownloadIQ = async () => {
    setIsDownloadingIQ(true);
    setIQDownloadHover(null);

    try {
      var user = downloadIQ.user === 'Accused' || downloadIQ.user === 'Defense Counsel' ? 'Accused' : 'Complainant'
      const request = {
        user: user,
        primaryID: downloadIQ.id,
        SecondaryID: selectedIds[1],
        generateFresh: false
      }

      const jwt = localStorage.getItem('jwtToken');
      const response = await axios.post(API_URL + `/fir/iq`, request, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
        responseType: 'blob', // Important: This tells axios to expect a binary file
      });

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'application/rtf' });

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = `IQ_${user}_FIR_${downloadIQ.id}.rtf`; // You can customize the file name here
      link.style.display = 'none'; // Hide the link

      // Add the link to the DOM, click it, and then remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the temporary URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading interrogation questions:', error);
      setErrorMessage("An error occurred while downloading the interrogation questions. Please try again later.");
    } finally {
      setDownloadIQ(null);
      setIsDownloadingIQ(false); //set this state variable to false
      setIQDownloadHover(null);
    }
  };

  const [uploadIR, setUploadIR] = useState(null);

  return (
    <div className="my-cases-container" style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh'
    }}>
      <Navbar />
      <main style={{ flex: 1 }}>
        <br></br><br></br>

        <div className="info-section" style={{ marginTop: '100px' }}>

<div className="info-section">
          <center>
            <div style={{ color: '#c62828', fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
              You are using the free version. For a more detailed analysis, Monthly and Annual paid subscriptions are available for Police Stations and District Police. Contact 9582822225 for pricing details.
            </div>
          </center>
        </div>
        
          <center>
            {/* <div hidden={noCasesFound}>
            <b>Case List</b>
            <br />
          </div> */}

            {!noCasesFound && !isLoading && (
              <div>
                <div
                  style={{
                    position: "relative",
                    textAlign: "center",
                    marginBottom: "16px", // replaces the <br/>
                  }}
                >
                  <b style={{ display: "inline-block" }}>Case List</b>
                  <button
                    onClick={fetchFirList}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      paddingLeft: "10px",
                      position: "relative",
                      top: "1px",
                    }}
                    title="Reload"
                  >
                    <RefreshCcw size={16} color="#094268" />
                  </button>
                </div>
              </div>
            )}
            <div hidden={!noCasesFound}>
              <b>No Cases Found</b>
              <button
                onClick={fetchFirList}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  paddingLeft: "10px",
                  position: "relative",
                  top: "1px",
                }}
                title="Reload"
              >
                <RefreshCcw size={16} color="#094268" />
              </button>
              <br />
            </div>

            {/* <span style={{ fontSize: "16px" }}>
            List of FIRs uploaded by you.
          </span> */}

          </center>
        </div>

        {isLoading && (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        )}

        {errorMessage && (
          <div className="error-popup" style={{ border: '2px solid white', backgroundColor: 'white', padding: '20px', borderRadius: '8px', textAlign: 'center', maxWidth: '400px', margin: '20px auto' }}>
            <div className="error-message" style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>{errorMessage}</div>
            <button
              className="error-ok-button"
              onClick={() => setErrorMessage(null)}
              style={{ padding: '10px 20px', fontSize: '14px', color: 'white', backgroundColor: '#007bff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              OK
            </button>
          </div>
        )}

        {!isLoading && !errorMessage && firList?.length === 0 && (
          <div className="info-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: "center" }}>
            <p>No cases found. Please upload a new FIR to start.</p>
            <button
              onClick={() => navigate("/upload")}
              style={{
                fontSize: "18px",
                //   color: "#1565C0", // Blue color
                // color: "#007bff",
                color: '#094268',
                background: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "bold"
              }}
            >
              Upload FIR <span style={{ fontSize: "20px" }}>→</span>
            </button>
          </div>
        )}

        {!isLoading && !errorMessage && firList?.length > 0 && (
          <div style={{ width: '100%' }}>
            <div style={{ alignContent: 'center', display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
              <button
                onClick={() => navigate("/upload")}
                hidden={isLoading}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#094268',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginRight: '16px',
                  fontWeight: 'bold',
                  paddingRight: '14px',
                }}
              >
                Analyse New FIR
              </button>
            </div>
            <div style={{ overflowX: 'auto', overflowY: 'visible', width: '100%', display: 'flex', justifyContent: 'center' }}>
              <table className="mycases-table">
                <thead>
                  <tr>
                    {/* <th>Select</th> */}
                    <th>S. No.</th>
                    <th hidden={!isAdmin}>User</th>
                    <th hidden={!isAdmin}>Record No.</th>
                    <th>Case Type</th>
                    <th>Complainant</th>
                    <th>Accused</th>
                    {/* <th>Section</th> */}
                    {/* <th hidden={localStorage.getItem('role') === 'Police'}>Analysis For</th> */}
                    <th>Uploaded On</th>
                    {/* <th>Language</th> */}
                    {/* <th hidden={!isAdmin}>Status</th> */}
                    <th>Analysis</th>
                    {/* <th>I.Q.</th>
                    <th>I.R.</th> */}
                    {/* <th>FIR</th> */}
                    {/* <th>FIR PDF</th> */}
                    <th>Share</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {firList.map((fir, index) => (
                    // <tr
                    //   key={fir.id}
                    //   style={{
                    //     backgroundColor: fir.id === '0dkuEsSZ1Hs9xIMjMqCf' || fir.id === 'y9z9xpAUJewPhl5JEfHD'
                    //       ? '#f0f0f0'  // Light grey background
                    //       : 'transparent' // Default (no background)
                    //   }}
                    // >
                    <tr
                      key={fir.id}
                      data-case-id={fir.id}
                      style={{
                        backgroundColor:
                          fir.id === selectedId
                            ? '#D6EAF8' // Highlight the row the user last navigated away from
                            : sampleFIR.includes(fir.id)
                              ? '#f0f0f0' // Light grey background
                              : showcaseFIR.includes(fir.id)
                                ? '#fcc6bb'     // Red background
                                : 'transparent', // Default (no background)
                        transition: 'background-color 0.3s ease',
                      }}
                    >
                      {/* <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(fir.id)}
                          // Disable unchecked boxes when already 2 are selected
                          disabled={!selectedIds.includes(fir.id) && maxSelected}
                          onChange={(e) => selectPairs(fir.id, e.target.checked)}
                          style={{ scale: 0.35 }}
                        />
                      </td> */}
                      <td style={{ padding: '12px 8px', verticalAlign: 'middle' }}>
                        {index + 1}
                      </td>
                      <td hidden={!isAdmin}>{fir.email}</td>
                      <td hidden={!isAdmin}>{fir.id}</td>
                      <td>{fir.caseType ? fir.caseType : 'Matrimonial'}</td>
                      <td>
                        {fir.complainant && fir.complainant.name
                          ? (fir.id === '0dkuEsSZ1Hs9xIMjMqCf' || fir.id === 'y9z9xpAUJewPhl5JEfHD'
                            ? <>{fir.complainant.name.length > 30 ? `${fir.complainant.name.substring(0, 30)}...` : fir.complainant.name} <span style={{ fontWeight: 'bold', color: '#094268' }}>(Sample)</span></>
                            : (fir.complainant.name.length > 30 ? `${fir.complainant.name.substring(0, 30)}...` : fir.complainant.name))
                          : '-'}
                      </td>
                      <td>
                        {fir.accusedList && fir.accusedList[0] && fir.accusedList[0].name
                          ? (fir.id === '0dkuEsSZ1Hs9xIMjMqCf' || fir.id === 'y9z9xpAUJewPhl5JEfHD'
                            ? <>{fir.accusedList[0].name.length > 30 ? `${fir.accusedList[0].name.substring(0, 30)}...` : fir.accusedList[0].name} <span style={{ fontWeight: 'bold', color: '#094268' }}>(Sample)</span></>
                            : (fir.accusedList[0].name.length > 30 ? `${fir.accusedList[0].name.substring(0, 30)}...` : fir.accusedList[0].name))
                          : '-'
                        }
                      </td>
                      {/* <td>{fir.firSections ? fir.firSections[0].number + ' ' + fir.firSections[0].act : '-'}</td> */}
                      {/* <td hidden={localStorage.getItem('role') === 'Police'}>{fir.user}</td> */}
                      <td>{fir.uploadedOn ? formatDateTime(fir.uploadedOn) : '-'}</td>
                      {/* <td>{fir.firLanguage ? fir.firLanguage : '-'}</td> */}
                      {/* <td hidden={!isAdmin} style={{ textAlign: "center", verticalAlign: "middle" }}>
                        {fir.witnessList ? (
                          <CheckCircle size={16} color="green" />
                        ) : (
                          <Clock size={16} color="orange" />
                        )}
                      </td> */}
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        <button
                          onClick={() => handleViewCase(fir)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          onMouseEnter={() => setViewHover(fir.id)}
                          onMouseLeave={() => setViewHover(null)}
                          title="View FIR AI Analysis"
                          aria-label="View FIR AI Analysis"
                        >
                          <FileText
                            size={16}
                            style={{
                              stroke: "#007bff", // Bootstrap blue
                              fill: viewHover === fir.id ? "#007bff" : "none", // fill on hover
                              transition: "fill 0.2s ease, stroke 0.2s ease",
                            }}
                          />
                        </button>
                      </td>
                      {/* <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <button
                          className="submit-button"
                          onClick={() => handleDownloadIQClick(fir)}
                          // disabled={selectedIds.length === 1 || !selectedIds.includes(fir.id) || (isDownloadingIQ && downloadIQ.id === fir.id)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            // cursor: selectedIds.length === 2 && selectedIds.includes(fir.id) ? "pointer" : "default",
                            position: "relative",
                          }}
                          onMouseEnter={() => setIQDownloadHover(fir.id)}
                          onMouseLeave={() => setIQDownloadHover(null)}
                          title="Download Interrogation Questions"
                          aria-label="Download Interrogation Questions"
                        >
                          {isDownloadingIQ && downloadIQ.id === fir.id && (
                            <div
                              style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100vw",
                                height: "100vh",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(0,0,0,0.3)", // semi-transparent overlay
                                zIndex: 9999,
                              }}
                            >
                              <div
                                className="spinner"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  border: "6px solid #f3f3f3",
                                  borderTop: "6px solid #3498db",
                                  borderRadius: "50%",
                                  animation: "spin 1s linear infinite",
                                }}
                              ></div>
                            </div>
                          )}
                          <Download
                            size={16}
                            style={{
                              // stroke: selectedIds.length === 2 && selectedIds.includes(fir.id) ? "#f37021" : "#d3d3d3", // orange if selected, grey if not
                              // stroke: "#f37021",
                              color: "#f37021",
                              fill:
                                // selectedIds.length === 2 && selectedIds.includes(fir.id) && iqDownloadHover === fir.id
                                iqDownloadHover === fir.id
                                  ? "#f37021"
                                  : "transparent", // only fill orange on hover if selected
                              // visibility:
                              //   downloadIQ && downloadIQ.id === fir.id ? "hidden" : "visible",
                              transition: "fill 0.2s ease, stroke 0.2s ease",
                            }}
                          />
                        </button>
                      </td> */}
                      {/* <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <button
                          className="submit-button"
                          onClick={() => handleUploadIRClick(fir)}
                          // disabled={selectedIds.length === 1 || !selectedIds.includes(fir.id) || (uploadIR && uploadIR.id === fir.id)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            // cursor: selectedIds.length === 2 && selectedIds.includes(fir.id) ? "pointer" : "default",
                            position: "relative",
                          }}
                          onMouseEnter={() => setIRUploadHover(fir.id)}
                          onMouseLeave={() => setIRUploadHover(null)}
                          title="Upload Interrogation Response"
                          aria-label="Upload Interrogation Response"
                        >
                          <Upload
                            size={16}
                            style={{
                              // stroke: selectedIds.length === 2 && selectedIds.includes(fir.id) ? "#09bec8" : "#d3d3d3", // orange if selected, grey if not
                              color: "09bec8",
                              fill:
                                // selectedIds.length === 2 && selectedIds.includes(fir.id) && irUploadHover === fir.id
                                irUploadHover === fir.id
                                  ? "#09bec8"
                                  : "transparent", // only fill orange on hover if selected
                              // visibility:
                              //   uploadIR && uploadIR.id === fir.id ? "hidden" : "visible",
                              transition: "fill 0.2s ease, stroke 0.2s ease",
                            }}
                          />
                        </button>
                      </td> */}
                      {/* <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        <button
                          onClick={() => handleViewFIR(fir)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          onMouseEnter={() => setFirViewHover(fir.id)}
                          onMouseLeave={() => setFirViewHover(null)}
                          title="View FIR Text"
                          aria-label="View FIR Text"
                        >
                          <Eye
                            size={16}
                            style={{
                              stroke: "#094268", // dark blue outline
                              fill: firViewHover === fir.id ? "#094268" : "none", // fill on hover
                              transition: "fill 0.2s ease, stroke 0.2s ease",
                            }}
                          />
                        </button>
                      </td> */}
                      {/* <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {!fir.bucketFileName ? (
                          <span style={{ fontSize: '16px', fontWeight: 'bold', alignContent: 'center', textAlign: 'center' }}>-</span>
                        ) : (
                          <button
                            className="submit-button"
                            onClick={() => handleDownloadClick(fir)}
                            disabled={isDownloading && downloadFIRID === fir.id} // Only disable while downloading
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                              position: "relative",
                            }}
                            onMouseEnter={() => setDownloadHover(fir.id)}
                            onMouseLeave={() => setDownloadHover(null)}
                            title="Download FIR PDF"
                            aria-label="Download FIR PDF"
                          >
                            {isDownloading && downloadFIRID === fir.id && (
                              <div
                                style={{
                                  position: "fixed",
                                  top: 0,
                                  left: 0,
                                  width: "100vw",
                                  height: "100vh",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "rgba(0,0,0,0.3)", // semi-transparent overlay
                                  zIndex: 9999,
                                }}
                              >
                                <div
                                  className="spinner"
                                  style={{
                                    width: "60px",
                                    height: "60px",
                                    border: "6px solid #f3f3f3",
                                    borderTop: "6px solid #3498db",
                                    borderRadius: "50%",
                                    animation: "spin 1s linear infinite",
                                  }}
                                ></div>
                              </div>
                            )}
                            <Download
                              size={16}
                              style={{
                                stroke: "#3498db",
                                fill: downloadHover === fir.id ? "#3498db" : "none",
                                visibility:
                                  isDownloading && downloadFIRID === fir.id ? "hidden" : "visible",
                                transition: "fill 0.2s ease, stroke 0.2s ease",
                              }}
                            />
                          </button>
                        )}
                      </td> */}

                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        <button
                          onClick={() => handleShareClick(fir)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          onMouseEnter={() => setShareHover(fir.id)}
                          onMouseLeave={() => setShareHover(null)}
                        >
                          <Share2
                            size={14}
                            style={{
                              stroke: "blue",
                              fill: shareHover === fir.id ? "blue" : "none",
                              transition: "fill 0.2s ease, stroke 0.2s ease",
                            }}
                          />
                        </button>
                      </td>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        <button
                          onClick={() => handleDeleteClick(fir)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          onMouseEnter={() => setDeleteHover(fir.id)}
                          onMouseLeave={() => setDeleteHover(null)}
                        >
                          <Trash
                            size={14}
                            style={{
                              stroke: "red",
                              fill: deleteHover === fir.id ? "red" : "none",
                              transition: "fill 0.2s ease, stroke 0.2s ease",
                            }}
                          />
                        </button>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!isLoading && !errorMessage && firList?.length > 0 && (
          <div className="info-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: "center" }}>
            <button
              onClick={() => navigate("/upload")}
              style={{
                fontSize: "18px",
                //   color: "#1565C0", // Blue color
                // color: "#007bff",
                color: '#094268',
                background: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "bold",
                paddingBottom: "100px"
              }}
            >
              Analyse New FIR <span style={{ fontSize: "20px" }}>→</span>
            </button>
          </div>
        )}

        {showDeleteConfirmPopup && firToDelete && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>{deleteErrorMessage ? 'Delete Failed' : deleteSuccessMessage ? 'Delete Successful' : 'Confirm Delete'}</h2>
              {deleteErrorMessage ? (
                <>
                  <p style={{ color: '#c9302c' }}>{deleteErrorMessage}</p>
                  <div className="popup-buttons">
                    <button className="submit-popup-button" onClick={handleCancelDelete}>
                      Close
                    </button>
                  </div>
                </>
              ) : deleteSuccessMessage ? (
                <>
                  <p style={{ color: '#2e7d32' }}>{deleteSuccessMessage}</p>
                  <div className="popup-buttons">
                    <button className="submit-popup-button" onClick={handleCancelDelete}>
                      OK
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p>Are you sure you want to delete the {firToDelete.caseType || 'selected'} FIR lodged by {firToDelete.complainant?.name || '-'}?</p>
                  <div className="popup-buttons">
                    <button className="submit-popup-button" onClick={handleCancelDelete}>
                      Cancel
                    </button>
                    <button className="cancel-button" onClick={handleConfirmDelete} disabled={isDeleting} style={{ minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isDeleting ? <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div> : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showShareConfirmPopup && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>{shareErrorMessage ? 'Share Failed' : shareSuccessMessage ? 'Share Successful' : 'Share FIR'}</h2>
              {shareErrorMessage ? (
                <>
                  <p style={{ color: '#c9302c' }}>{shareErrorMessage}</p>
                  <div className="popup-buttons">
                    <button
                      className="submit-popup-button"
                      onClick={() => {
                        setShowShareConfirmPopup(false);
                        setShareEmail(null);
                        setShareErrorMessage(null);
                        setShareSuccessMessage(null);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </>
              ) : shareSuccessMessage ? (
                <>
                  <p style={{ color: '#2e7d32' }}>{shareSuccessMessage}</p>
                  <div className="popup-buttons">
                    <button
                      className="submit-popup-button"
                      onClick={() => {
                        setShowShareConfirmPopup(false);
                        setShareEmail(null);
                        setShareErrorMessage(null);
                        setShareSuccessMessage(null);
                      }}
                    >
                      OK
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <input
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    className="poc-input"
                    placeholder="Please enter the LegalApp User ID"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && shareEmail && shareEmail.length >= 4) {
                        e.preventDefault();
                        handleConfirmShare();
                      }
                    }}
                  />
                  <div className="popup-buttons">
                    <button
                      className="cancel-button"
                      onClick={() => {
                        setShowShareConfirmPopup(false);
                        setShareEmail(null);
                        setShareErrorMessage(null);
                        setShareSuccessMessage(null);
                      }}
                      style={{ minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >Cancel</button>
                    <button
                      className="submit-popup-button"
                      onClick={handleConfirmShare}
                      disabled={shareEmail && shareEmail.length < 4}
                      style={{ minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {shareLoading ? <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div> : "Share"}
                    </button>
                  </div>
                </>
              )}
              {/* <h5>Please email contact@legalapp.in to get the analysis voucher.</h5> */}
            </div>
          </div>
        )}

        {downloadIQ && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Confirm</h2>
              <p>Download Interrogation Questions for {downloadIQ.user === 'Complainant' || downloadIQ.user === 'Prosecution Counsel' ? 'Complainant' : 'Accused'}?</p>
              <div className="popup-buttons">
                <button className="cancel-button" onClick={() => setDownloadIQ(null)}>
                  Cancel
                </button>
                <button className="submit-popup-button" onClick={handleDownloadIQ}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {uploadIR && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Confirm</h2>
              <p>Upload Interrogation Response of {uploadIR.user === 'Complainant' || uploadIR.user === 'Prosecution Counsel' ? 'Complainant' : 'Accused'} or their Witness?</p>
              <p>To ensure accuracy, upload the Interrogation Response or Statement of only one person at a time.</p>
              <div className="popup-buttons">
                <button className="cancel-button" onClick={() => setUploadIR(null)}>
                  Cancel
                </button>
                <button className="submit-popup-button"
                  onClick={() => {
                    const responseGivenBy = ['Complainant', 'Prosecution Counsel'].includes(uploadIR?.user)
                      ? 'Complainant / Witness'
                      : 'Accused / Witness';
                    const primaryFIR = selectedIds[0];
                    const secondaryFIR = selectedIds[1];
                    navigate('/response', { state: { responseGivenBy, primaryFIR, secondaryFIR } });
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {pairNotFound && (
          <div className="popup-overlay">
            <div className="popup-container">
              <h2>Error</h2>
              <p>Please complete the FIR analysis for the <b>{pairMissing}</b> before proceeding.</p>
              <div className="popup-buttons">
                <button className="submit-popup-button" onClick={() => setPairNotFound(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmMessage && (
          <div className="popup-overlay">
            <div className="popup-container">
              <p>{confirmMessage}</p>
              <div className="popup-buttons">
                <button className="submit-popup-button" onClick={() => setConfirmMessage(null)}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* <div style={{ height: '100px' }}></div> */}
      </main>
      <Footer />
    </div>
  );
};

export default MyCases;
